import React, { useState, useEffect } from 'react';
import { Checkbox, Input } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import s from './s.module.less';

interface MedicationCardProps {
    condition: string;
    medications?: string[];
    nonPrescribeMedications?: string[];
    onChange: (checkedValues: string[], unCheckedValues: string[]) => void;
    isOpen: boolean;
    onToggle: () => void;
}

const MedicationCard: React.FC<MedicationCardProps> = ({
    condition,
    medications,
    nonPrescribeMedications,
    onChange,
    isOpen,
    onToggle,
}) => {
    const [selectedMedications, setSelectedMedications] = useState<string[]>([]);

    useEffect(() => {
        if (medications?.length) {
            setSelectedMedications(medications.filter((item) => nonPrescribeMedications?.includes(item)));
        } else {
            setSelectedMedications(nonPrescribeMedications?.length ? [...nonPrescribeMedications] : []);
        }
    }, [nonPrescribeMedications, medications]);

    const onMedicationsChange = (values: string[]) => {
        setSelectedMedications(values);
        onChange(values, medications?.filter((item) => !values.includes(item)) || []);
    };

    const onOtherMedicationsChange = (value: string) => {
        const vals = value.split(', ');
        setSelectedMedications(vals);
        onChange(vals, nonPrescribeMedications?.filter((item) => !vals.includes(item)) || []);
    };

    const selectAll = selectedMedications.length === medications?.length;
    const isOther = !medications?.length;

    return (
        <div className={s.medicationCard}>
            <div className={s.header} onClick={onToggle}>
                <div className={s.condition}>
                    <span className={s.condition}>{condition}</span>
                    {selectedMedications.length > 0 && (
                        <span className={s.medicationCountLabel}>Medications excluded</span>
                    )}
                </div>
                <span className={s.expandIcon}>{isOpen ? <UpOutlined /> : <DownOutlined />}</span>
            </div>
            {isOpen && (
                <div className={s.content}>
                    {!isOther ? (
                        <>
                            <div className={s.medicationCount}>
                                {selectedMedications.length} medications selected
                            </div>
                            <div className={s.medicationSelectSection}>
                                <div className={s.medicationHeader}>
                                    <span className={s.title}>Select medications you don’t prescribe</span>
                                    <div className={s.actions}>
                                        <Checkbox
                                            checked={selectAll}
                                            indeterminate={false}
                                            onChange={(e) => onMedicationsChange(e.target.checked ? medications : [])}
                                        >
                                            Select all
                                        </Checkbox>
                                        <span
                                            className={s.resetBtn}
                                            onClick={() => onMedicationsChange([])}
                                        >
                                            Reset
                                        </span>
                                    </div>
                                </div>
                                <div className={s.listWrapper}>
                                    <Checkbox.Group
                                        value={selectedMedications}
                                        onChange={(values) => {
                                            onMedicationsChange(values as string[]);
                                        }}
                                        className={s.medicationList}
                                    >
                                        {medications.map((med) => (
                                            <Checkbox key={med} value={med}>{med}</Checkbox>
                                        ))}
                                    </Checkbox.Group>
                                </div>
                            </div>
                        </>) :
                        <div className={s.otherMedications}>
                            <span className={s.otherMedicationsText}>Add any medications you don’t prescribe (not listed above)</span>
                            <Input
                                value={selectedMedications.join(', ')}
                                onChange={(e) => onOtherMedicationsChange(e.target.value)}
                                placeholder="Add medication names and separate by comma"
                            />
                        </div>}
                </div>
            )}
        </div>
    );
};

export default MedicationCard;
